<template>
    <div class="seedance-model-page">
        <!-- Video Generator Section -->
        <section ref="generatorSection" class="video-generator-section">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <h1>{{ t('seedanceProFast.generatorTitle') }}</h1>
                            <p class="generator-description" v-html="t('seedanceProFast.generatorDescription')
                                "></p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <VideoGenerator model-name="Seedance 1.0 Pro Fast" :tabs="tabs" redirect-url="/ai-video"
                        @generate="handleGenerate">
                        <template #model-logo>
                            <div class="seedance-logo">S</div>
                        </template>
                    </VideoGenerator>
                </div>
            </div>
        </section>

        <!-- Core Features Section -->
        <section class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedanceProFast.coreFeaturesTitle') }}
                    </h2>
                </div>

                <div class="features-showcase-container">
                    <!-- Feature 1: 精准指令解析与执行 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'seedanceProFast.preciseInstructionTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedanceProFast.preciseInstructionDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.preciseInstruction"
                                    :poster="getResourceThumbnail(demoVideos.preciseInstruction)"
                                    :title="
                                        t(
                                            'seedanceProFast.preciseInstructionTitle'
                                        )
                                    "
                                    :description="
                                        t(
                                            'seedanceProFast.preciseInstructionDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 2: 无缝多镜头叙事能力 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.multiShotNarrative"
                                    :poster="getResourceThumbnail(demoVideos.multiShotNarrative)"
                                    :title="
                                        t(
                                            'seedanceProFast.multiShotNarrativeTitle'
                                        )
                                    "
                                    :description="
                                        t(
                                            'seedanceProFast.multiShotNarrativeDescription'
                                        )
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'seedanceProFast.multiShotNarrativeTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedanceProFast.multiShotNarrativeDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Feature 3: 细节真实性与风格一致性 -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('seedanceProFast.detailRealismTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedanceProFast.detailRealismDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.detailRealism"
                                    :poster="getResourceThumbnail(demoVideos.detailRealism)"
                                    :title="t('seedanceProFast.detailRealismTitle')"
                                    :description="
                                        t(
                                            'seedanceProFast.detailRealismDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Feature 4: 复杂运动稳定性强化 -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.complexMotionStability"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.complexMotionStability
                                        )
                                    "
                                    :title="
                                        t(
                                            'seedanceProFast.complexMotionStabilityTitle'
                                        )
                                    "
                                    :description="
                                        t(
                                            'seedanceProFast.complexMotionStabilityDescription'
                                        )
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t(
                                            'seedanceProFast.complexMotionStabilityTitle'
                                        )
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'seedanceProFast.complexMotionStabilityDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- How to Use Section -->
        <section class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedanceProFast.howToUseTitle') }}
                    </h2>
                </div>

                <div class="steps-container">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('seedanceProFast.step1Title') }}
                            </h3>
                            <p class="step-description" v-html="t('seedanceProFast.step1Description')"></p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('seedanceProFast.step2Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('seedanceProFast.step2Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('seedanceProFast.step3Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('seedanceProFast.step3Description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- YouTube Videos Section -->
        <section class="youtube-videos-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedanceProFast.youtubeTitle') }}
                    </h2>
                </div>

                <div class="youtube-carousel">
                    <button class="carousel-nav-btn carousel-nav-left" @click="previousVideo"
                        :aria-label="t('common.previous')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div class="videos-wrapper">
                        <div class="videos-scroll" :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }">
                            <div v-for="(video, index) in youtubeVideos" :key="index" class="video-iframe-container">
                                <iframe :src="video.embedUrl" :title="video.title" class="video-iframe" frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen></iframe>
                            </div>
                        </div>
                    </div>

                    <button class="carousel-nav-btn carousel-nav-right" @click="nextVideo"
                        :aria-label="t('common.next')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </section>

        <!-- X Posts Section -->
        <TwitterPosts :title="t('seedanceProFast.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <section class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedanceProFast.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/seedance" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/98653aa87810721ed41e47d1df57b068.png"
                                :alt="t('seedanceProFast.seedanceTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedanceProFast.seedanceTitle') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/hailuo-23" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/2b9d9924fb8b25932b804c7fe99d792d.png"
                                :alt="t('seedanceProFast.hailuo23Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedanceProFast.hailuo23Title') }}
                        </h3>
                    </NuxtLink>

                    <NuxtLink to="/video/sora/sora-2" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                :alt="t('seedanceProFast.sora2Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('seedanceProFast.sora2Title') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </section>

        <!-- FAQ Section -->
        <section class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('seedanceProFast.faqTitle') }}
                    </h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- CTA Section -->
        <section class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">
                            {{ t('seedanceProFast.ctaTitle') }}
                        </h2>
                        <p class="cta-description">
                            {{ t('seedanceProFast.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('seedanceProFast.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('seedanceProFast.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import '~/assets/css/howToUse.css'
import { getResourceThumbnail } from '~/utils/common'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    preciseInstruction:
        'https://static.weryai.com/growth/video/b5f425f13b8945dfb4c18895f5805f7b.mp4',
    multiShotNarrative:
        'https://static.weryai.com/growth/video/8a4e489467f03e95b528dbba9d2feac5.mp4',
    detailRealism:
        'https://static.weryai.com/growth/video/201ca9b1031439dec00ed086787a5b99.mp4',
    complexMotionStability:
        'https://static.weryai.com/growth/video/604f7d8fa2db50915f60e809ddca2991.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'seedance_pro_fast' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('seedanceProFast.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// SEO Meta Data
useHead({
    title: computed(() => t('seedanceProFast.pageTitle') + ' | 微鲤AI'),
    meta: [
        {
            name: 'description',
            content: computed(() => t('seedanceProFast.pageDescription')),
        },
        {
            name: 'keywords',
            content: computed(() => t('seedanceProFast.pageKeywords')),
        },
        {
            property: 'og:title',
            content: computed(() => t('seedanceProFast.pageTitle')),
        },
        {
            property: 'og:description',
            content: computed(() => t('seedanceProFast.pageDescription')),
        },
        {
            property: 'og:type',
            content: 'website',
        },
        {
            name: 'twitter:card',
            content: 'summary_large_image',
        },
        {
            name: 'twitter:title',
            content: computed(() => t('seedanceProFast.pageTitle')),
        },
        {
            name: 'twitter:description',
            content: computed(() => t('seedanceProFast.pageDescription')),
        },
    ],
    link: [
        {
            key: 'canonical',
            rel: 'canonical',
            href: 'https://www.weryai.com.cn/video/seedance-pro-fast',
        },
    ],
})

// Structured Data for SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: 'Seedance 1.0 Pro Fast AI 视频生成器',
                description:
                    '火山引擎视频生成模型，在生成速度与成本效益上实现突破性升级，助力企业与创作者高效规模化创作视频内容',
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                provider: {
                    '@type': 'Organization',
                    name: '微鲤AI',
                    url: 'https://www.weryai.com.cn',
                },
                featureList: [
                    '精准指令解析与执行',
                    '无缝多镜头叙事能力',
                    '细节真实性与风格一致性',
                    '复杂运动稳定性强化',
                ],
            }),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('seedanceProFast.tabImageToVideo'), external: false },
    { name: t('seedanceProFast.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// HowTo Steps for SEO
const howToUseSteps = computed(() => [
    {
        title: t('seedanceProFast.step1Title'),
        description: t('seedanceProFast.step1Description'),
    },
    {
        title: t('seedanceProFast.step2Title'),
        description: t('seedanceProFast.step2Description'),
    },
    {
        title: t('seedanceProFast.step3Title'),
        description: t('seedanceProFast.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('seedanceProFast.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const currentVideoIndex = ref(0)

const youtubeVideos = ref([
    {
        title: 'Seedance 1.0 Pro Fast - Complete Overview',
        embedUrl: 'https://www.youtube.com/embed/w1yYKPuW520',
    },
    {
        title: 'Seedance 1.0 Pro Fast Tutorial Guide',
        embedUrl: 'https://www.youtube.com/embed/8EkC_SQE6jM',
    },
    {
        title: 'Seedance 1.0 Pro Fast Features Explained',
        embedUrl: 'https://www.youtube.com/embed/KTOdcWeyvyA',
    },
    {
        title: 'Getting Started with Seedance 1.0 Pro Fast',
        embedUrl: 'https://www.youtube.com/embed/tp-uXz83H3w',
    },
    {
        title: 'Seedance 1.0 Pro Fast Advanced Tips',
        embedUrl: 'https://www.youtube.com/embed/sHCszNjVpOc',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://x.com/shamimai1/status/1983806608212946990',
    'https://x.com/replicate/status/1981696564386259189',
    'https://x.com/TechByMarkandey/status/1983802280630743395',
    'https://x.com/BytePlusGlobal/status/1981695335610089702',
    'https://x.com/angrypenguinPNG/status/1981871603744137417',
    'https://x.com/Creator_Toolbox/status/1983745332807897564',
    'https://x.com/ai_for_success/status/1983543975077368260',
    'https://x.com/Forestyanyan/status/1983440959045292353',
    'https://x.com/fofrAI/status/1981700275909116362',
]

// Carousel Methods
const nextVideo = () => {
    // Maximum 3 visible videos, so max index is 2 (3rd video)
    const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0 // Back to first video
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        // Back to last visible video position
        const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// Page Mounted
onMounted(() => {
    // Page view analytics tracking
    analyticsEvent('page_view_seedance_pro_fast')
    analyticsEvent('view_model_page_common', {
        model_name: 'seedance_1_0_pro_fast',
        type: 'video',
    })
})

// FAQ Data
const faqData = computed(() => [
    {
        question: t('seedanceProFast.faq1Question'),
        answer: t('seedanceProFast.faq1Answer'),
    },
    {
        question: t('seedanceProFast.faq2Question'),
        answer: t('seedanceProFast.faq2Answer'),
    },
    {
        question: t('seedanceProFast.faq3Question'),
        answer: t('seedanceProFast.faq3Answer'),
    },
    {
        question: t('seedanceProFast.faq4Question'),
        answer: t('seedanceProFast.faq4Answer'),
    },
    {
        question: t('seedanceProFast.faq5Question'),
        answer: t('seedanceProFast.faq5Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}
</script>

<style lang="less" scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

.seedance-model-page {
    color: white;
}

/* Core Features Section */
.core-features-section {
    padding: 6rem 0;
    background-color: rgb(21, 10, 11);
}

.features-showcase-container {
    max-width: 1200px;
    margin: 0 auto;
}

.feature-showcase {
    margin-bottom: 6rem;

    &:last-of-type {
        margin-bottom: 4rem;
    }
}

.feature-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    max-width: 1200px;
    margin: 0 auto;

    &.reverse {
        grid-template-columns: 1fr 1fr;

        .feature-video {
            order: 1;
        }

        .feature-text {
            order: 2;
        }
    }
}

.feature-text {
    padding: 2rem;
}

.feature-title {
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: white;
    line-height: 1.3;
}

.feature-description {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    font-size: 1.1rem;
    margin: 0;
}

.feature-video {
    position: relative;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

/* How to Use Section */
.how-to-use-section {
    padding: 6rem 0;
    background: rgba(255, 255, 255, 0.02);
}

.steps-container {
    display: flex;
    justify-content: space-between;
    position: relative;
    gap: 2rem;
    max-width: 1400px;
    margin: 0 auto;
    padding: 5rem;
    border-radius: 8px;
    overflow: hidden;
}

/* 设置背景图片 */
.steps-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url('https://static.weryai.com/growth/image/28cd14ee5659acb6b48ff315a8709737.webp');
    background-size: cover;
    background-position: center;
    z-index: -2;
}

/* 背景图片半透明遮罩层 */
.steps-container::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to bottom,
            rgba(22, 20, 33, 1) 0%,
            rgba(22, 20, 33, 1) 10%,
            rgba(22, 20, 33, 0.7) 30%,
            rgba(22, 20, 33, 0.7) 50%,
            rgba(22, 20, 33, 0.3) 100%);
    z-index: -1;
}

.step-item {
    flex: 1;
    text-align: left;
    backdrop-filter: blur(10px);
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 1rem 2rem;
}

.step-number {
    width: 3rem;
    height: 3rem;
    position: relative;
    background-color: #252527;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    color: white;
    margin: 0 auto 0.75rem;
    margin-left: 5px;
}

/* 2. 外层环形（伪元素实现） */
.step-number::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    box-shadow: 4px 4px 6px rgba(255, 255, 255, 0.4) inset;
    z-index: -1;
    border-top: 1px solid rgba(255, 255, 255, 0.8);
}

.step-title {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: white;
}

.step-description {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
}

.highlight-text {
    color: #ec4899;
    font-weight: 600;
}

/* Video Generator Section */
.video-generator-section {
    padding: 0;
    background: transparent;
    min-height: calc(100vh - 120px);
    position: relative;
}

.generator-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 0 1.5rem;
    position: relative;
    z-index: 1;
}

.generator-header {
    text-align: center;
    padding: 1rem 0 1rem;
    margin-bottom: 0;
}

.generator-title {
    text-align: center;
    margin-bottom: 0;
}

.generator-title h1 {
    font-size: 3.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 0.75rem;
    line-height: 1.2;
}

.generator-description {
    font-size: 1rem;
    color: #adadad;
    max-width: 700px;
    margin: 0 auto 0.75rem;
    line-height: 1.5;
    text-align: center;
}

.highlight-bold {
    color: #ef4444;
    font-weight: 600;
}

.rainbow-bar {
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg,
            #ef4444,
            #f97316,
            #eab308,
            #22c55e,
            #3b82f6,
            #8b5cf6);
    border-radius: 2px;
    margin: 0 auto 1rem;
    position: relative;
}

.rainbow-bar::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
}

.seedance-logo {
    width: 1.25rem;
    height: 1.25rem;
    background: linear-gradient(135deg, #10b981, #059669);
    border-radius: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 700;
    color: white;
}

/* YouTube Videos Section */
.youtube-videos-section {
    padding: 6rem 0;
    background-color: #22242d;
}

.youtube-carousel {
    position: relative;
    max-width: 1400px;
    margin: 0 auto;
}

.carousel-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.2);
        border-color: rgba(255, 255, 255, 0.4);
    }

    &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
}

.carousel-nav-left {
    left: -1.25rem;
}

.carousel-nav-right {
    right: -1.25rem;
}

.videos-wrapper {
    overflow: hidden;
    border-radius: 0.75rem;
}

.videos-scroll {
    display: flex;
    transition: transform 0.5s ease;
    gap: 1rem;
}

.video-iframe-container {
    flex: 0 0 33.333%;
    min-width: 0;
}

.video-iframe {
    width: 100%;
    height: 200px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;

    @media (min-width: 640px) {
        height: 296px;
    }
}

/* Explore Other Models Section */
.explore-models-section {
    padding: 6rem 0;
    background-color: #0d0e12;
}

.models-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    max-width: 1000px;
    margin: 0 auto;
}

.model-card {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
    padding: 2rem;
    text-decoration: none;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.2);
        transform: translateY(-4px);
    }
}

.model-icon {
    width: 4rem;
    height: 4rem;
    background: linear-gradient(135deg, #10b981, #059669);
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    margin-bottom: 1.5rem;
}

.model-card-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    line-height: 1.4;
    margin: 0;
}

/* FAQ Section */
.faq-section {
    padding: 6rem 0;
    background: rgba(255, 255, 255, 0.02);
}

.faq-list {
    max-width: 800px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    row-gap: 1rem;
}

.faq-item {
    padding: 2rem 0;
    background-color: #22242d;
    border-radius: 8px;

    &:last-child {
        border-bottom: none;
    }
}

.faq-question {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
    transition: color 0.2s ease;
    padding-left: 2rem;
    color: #f5f2f0;

    &:hover {
        color: #60a5fa;
    }
}

.faq-icon {
    font-size: 1.25rem;
    font-weight: bold;
    color: #60a5fa;
    transition: transform 0.2s ease;
}

.faq-item.active .faq-icon {
    transform: rotate(90deg);
}

.faq-text {
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
}

.faq-answer {
    padding-left: 2rem;
    opacity: 0;
    max-height: 0;
    overflow: hidden;
    transition: all 0.3s ease;
    color: #adadad;
}

.faq-item.active .faq-answer {
    opacity: 1;
    max-height: 500px;
    padding-top: 1rem;
}

.faq-answer p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    margin: 0;
}

/* CTA Section */
.cta-section {
    padding: 6rem 0;
    background: linear-gradient(135deg, #1d1b28 0%, #141218 50%, #121117 100%);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
            radial-gradient(circle at 30% 20%,
                rgba(59, 130, 246, 0.1) 0%,
                transparent 50%),
            radial-gradient(circle at 70% 80%,
                rgba(139, 92, 246, 0.1) 0%,
                transparent 50%);
        pointer-events: none;
    }
}

.cta-content {
    text-align: center;
    position: relative;
    z-index: 1;
}

.cta-header {
    margin-bottom: 3rem;
}

.cta-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
    color: #f5f2f0;
    line-height: 1.2;
    background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.cta-description {
    font-size: 1.25rem;
    color: #adadad;
    margin-bottom: 0;
    line-height: 1.6;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
}

.cta-logo {
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
}

.logo-icon {
    width: 4rem;
    height: 4rem;
    display: flex;
    align-items: center;
    justify-content: center;

    .logo-image {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

.logo-icon.logo-image {
    width: 6.25rem;
    height: 6.25rem;
}

.cta-actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;
}

.cta-primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 6rem;
    background: linear-gradient(120deg, #ff85b8, #ff5a8f);
    color: white;
    text-decoration: none;
    border-radius: 0.75rem;
    font-size: 1.125rem;
    font-weight: 600;
    transition: all 0.3s ease;
    cursor: pointer;
    box-shadow: 0 8px 32px rgba(255, 255, 255, 0.4) inset;
    border: none;
    position: relative;
    overflow: hidden;
    border: 1px solid #fff;
    z-index: 1;

    &::before {
        content: '';
        position: absolute;
        inset: -2px;
        border-radius: 0.75rem;
        background: linear-gradient(180deg,
                rgba(0, 0, 0, 0.8),
                rgba(0, 0, 0, 0.6),
                rgba(0, 0, 0, 0.1),
                rgba(0, 0, 0, 0));
        z-index: -1;
        opacity: 0.3;
        filter: blur(8px);
    }

    &:hover {
        background: linear-gradient(135deg, #f472b6 0%, #ec4899 100%);
        transform: translateY(-2px);
        box-shadow: 0 12px 40px rgba(236, 72, 153, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
}

.cta-disclaimer {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    margin: 0;
    font-weight: 400;
}

/* Responsive Design */
@media (max-width: 768px) {
    .generator-title h1 {
        font-size: 2rem;
    }

    .feature-content {
        grid-template-columns: 1fr;
        gap: 2rem;

        &.reverse {
            grid-template-columns: 1fr;

            .feature-video {
                order: 1;
            }

            .feature-text {
                order: 2;
            }
        }
    }

    .feature-text {
        padding: 1rem;
    }

    .feature-title {
        font-size: 1.5rem;
        margin-bottom: 1rem;
    }

    .feature-description {
        font-size: 1rem;
    }

    .steps-container {
        flex-direction: column;
        gap: 2rem;
    }

    .section-title {
        font-size: 2.5rem;
    }

    .video-iframe-container {
        flex: 0 0 50%;
    }
}

@media (max-width: 480px) {
    .generator-title h1 {
        font-size: 1.75rem;
    }

    .section-title {
        font-size: 2rem;
    }

    .cta-title {
        font-size: 1.875rem;
    }

    .video-iframe-container {
        flex: 0 0 100%;
    }
}
</style>
